/**
 * Sensible UX defaults for the propose forms' DATE fields — and ONLY the date fields.
 *
 * A blank "valid from today, reviewed in six months, expires in a year" is a convenience
 * about the SHAPE of a validity window, with no commercial content of its own — a planner
 * who leaves these untouched gets a normal window, and one who has a different window in
 * mind edits three date pickers, not a hidden assumption.
 *
 * A parameter's authored VALUE is the opposite of that. `parameters.ts`'s own module
 * docstring (ADR 2000) and `docs/adr/2001` both say why: G1 makes a tunable that arrived
 * with an invented value a defect, so `numericValueText`/`textValue` are never touched by
 * anything in this file, and nothing here is imported by a value field's initial state.
 *
 * Pure and takes `now` as an argument rather than reading the clock itself, so a test
 * asserts on a fixed instant rather than on whatever day it happens to run.
 */

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `now`, as the `YYYY-MM-DD` this registry's date fields hold. */
export function todayIso(now: Date): string {
  return isoDate(now);
}

/** `now` plus `days`, as an ISO calendar date. UTC throughout, so the result does not
 *  depend on the caller's timezone. */
export function addDaysIso(now: Date, days: number): string {
  const next = new Date(now.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return isoDate(next);
}

/** The review-date default this screen offers: six months out. A registry review date is
 *  "a commitment, not a label" (`17` §5) and six months is a planning-horizon-scaled guess
 *  a human is expected to override, not a governed number. */
export const DEFAULT_REVIEW_HORIZON_DAYS = 180;

/** The validity-window default this screen offers: a year. Same status as the review
 *  horizon above — a starting point for the date pickers, never a value the register
 *  reads. */
export const DEFAULT_VALIDITY_HORIZON_DAYS = 365;
